import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { submitContactForm } from '../emailService';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../components/ui/navigation-menu';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import { Search, Menu, X, Sparkles, ArrowRight, CheckCircle2, Zap, TrendingUp, AlertTriangle, BarChart3, Settings, Target, Rocket } from 'lucide-react';

const Logo = () => (
  <div className="flex items-center gap-3 group cursor-pointer">
    {/* <img 
      src="https://customer-assets.emergentagent.com/job_3e6009bf-ed44-4504-b0d6-0c0310b2410a/artifacts/fygtkb8x_whyAIrecommend_logo.png" 
      alt="whyAIrecommend logo" 
      className="h-12 w-auto drop-shadow-md"
    /> */}
    <span className="text-xl font-bold tracking-tight text-foreground bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent group-hover:from-primary group-hover:to-primary/70 transition-all">
      whyAIrecommend
    </span>
  </div>
);

const Header = () => {
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 shadow-lg">
        <div className="container flex h-20 items-center justify-between px-4">
          <div className="flex items-center gap-8">
            <Logo />
            <NavigationMenu className="hidden lg:flex">
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-all hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 hover:scale-105"
                    href="#problem"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('problem')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    The Problem
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-all hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 hover:scale-105"
                    href="#how-it-works"
                  >
                    How It Works
                  </NavigationMenuLink>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <NavigationMenuLink
                    className="group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-all hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 hover:scale-105"
                    href="#get-started"
                  >
                    Get Started
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="hidden sm:flex hover:bg-accent hover:scale-110 transition-all"
              onClick={() => setSearchOpen(true)}
            >
              <Search className="h-5 w-5" />
              <span className="sr-only">Search</span>
            </Button>
            <Button 
              variant="default"
              size="default"
              className="hidden sm:flex bg-gradient-to-r from-primary to-primary/90 hover:shadow-lg hover:scale-105 transition-all"
              onClick={() => document.getElementById('get-started').scrollIntoView({ behavior: 'smooth' })}
            >
              Get started
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden hover:bg-accent hover:scale-110 transition-all"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              <span className="sr-only">Toggle menu</span>
            </Button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t bg-background">
            <div className="container px-4 py-4 space-y-3">
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={() => {
                  document.getElementById('problem')?.scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
              >
                The Problem
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={() => {
                  document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
              >
                How It Works
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start"
                onClick={() => {
                  document.getElementById('get-started')?.scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
              >
                Get Started
              </Button>
              <Button
                variant="default"
                className="w-full"
                onClick={() => {
                  document.getElementById('get-started')?.scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
              >
                Get your free check
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Search Dialog */}
      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen}>
        <CommandInput placeholder="Search..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          <CommandGroup heading="Navigation">
            <CommandItem onSelect={() => {
              document.getElementById('problem')?.scrollIntoView({ behavior: 'smooth' });
              setSearchOpen(false);
            }}>
              The Problem
            </CommandItem>
            <CommandItem onSelect={() => {
              document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
              setSearchOpen(false);
            }}>
              How It Works
            </CommandItem>
            <CommandItem onSelect={() => {
              document.getElementById('get-started')?.scrollIntoView({ behavior: 'smooth' });
              setSearchOpen(false);
            }}>
              Get Started
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
};

const HeroSection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section className="relative container space-y-6 py-20 md:py-32 lg:py-40 overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background" />
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(0, 0, 0, 0.1), transparent 40%)`
          }}
        />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>
      
      <div className="mx-auto flex max-w-[64rem] flex-col items-center gap-8 text-center">
        <div className="inline-flex items-center rounded-full border bg-gradient-to-r from-background to-muted/50 px-4 py-2 text-sm font-medium shadow-lg backdrop-blur-sm animate-in fade-in slide-in-from-bottom-4 duration-1000 hover:scale-105 transition-transform">
          <Sparkles className="mr-2 h-4 w-4 text-primary animate-pulse" />
          AI Recommendation Audits
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-150">
          Why does AI recommend{' '}
          <span className="relative inline-block">
            <span className="relative z-10 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
              your competitors
            </span>
            <span className="absolute bottom-0 left-0 right-0 h-3 bg-primary/20 -rotate-1 transform origin-left" />
          </span>
          , not you?
        </h1>
        <p className="max-w-[42rem] leading-normal text-muted-foreground sm:text-xl sm:leading-8 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-300">
          AI tools like ChatGPT and Perplexity now recommend only 2–3 tools instead of ranking 10 links. 
          If AI doesn't recommend your product, your target customers may never find you.
        </p>
        <div className="flex flex-col gap-4 sm:flex-row animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-500">
          <Button 
            size="lg"
            className="h-12 px-8 text-base shadow-lg hover:shadow-xl transition-all hover:scale-105 bg-gradient-to-r from-primary to-primary/90 group"
            onClick={() => document.getElementById('get-started').scrollIntoView({ behavior: 'smooth' })}
          >
            Get your free AI recommendation check
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

const ProblemSection = () => (
  <section id="problem" className="relative container space-y-6 py-20 md:py-32">
    <div className="absolute inset-0 -z-10 bg-gradient-to-r from-transparent via-muted/10 to-transparent" />
    <div className="mx-auto flex max-w-[64rem] flex-col items-center gap-8">
      <div className="grid gap-8 md:grid-cols-2 md:gap-16 items-center">
        <div className="space-y-6">
          <div className="inline-flex items-center rounded-full border bg-gradient-to-r from-muted to-muted/80 px-4 py-2 text-sm font-medium shadow-md backdrop-blur-sm hover:shadow-lg transition-all group">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 mr-2 group-hover:bg-primary/20 transition-colors">
              <AlertTriangle className="h-3.5 w-3.5 text-primary" />
            </div>
            The Problem
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
            AI answers are replacing{' '}
            <span className="relative inline-block">
              <span className="relative z-10">Google search</span>
              <span className="absolute bottom-2 left-0 right-0 h-3 bg-primary/20 -rotate-1 transform origin-left" />
            </span>
          </h2>
        </div>
        <div className="space-y-6 text-left">
          <div className="group relative pl-6 border-l-4 border-primary/30 hover:border-primary/60 transition-colors rounded-r-lg bg-gradient-to-r from-transparent to-muted/30 p-4 hover:shadow-md">
            <div className="absolute left-0 top-4 w-4 h-4 rounded-full bg-primary/20 group-hover:bg-primary/40 transition-colors -translate-x-[10px]" />
            <p className="text-lg leading-8 text-muted-foreground">
              When users ask <span className="font-semibold text-foreground">"What's the best CRM for startups?"</span> they no longer scroll through 10 blue links. 
              They get a direct answer with 2–3 recommendations.
            </p>
          </div>
          <div className="group relative pl-6 border-l-4 border-primary/30 hover:border-primary/60 transition-colors rounded-r-lg bg-gradient-to-r from-transparent to-muted/30 p-4 hover:shadow-md">
            <div className="absolute left-0 top-4 w-4 h-4 rounded-full bg-primary/20 group-hover:bg-primary/40 transition-colors -translate-x-[10px]" />
            <p className="text-lg leading-8 text-muted-foreground">
              Founder-led SaaS products are becoming invisible if AI does not recommend them. Your SEO efforts, 
              your content strategy—none of it matters if AI assistants don't know you exist.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const SampleResultsSection = () => (
  <section className="relative container space-y-6 py-20 md:py-32 bg-gradient-to-b from-muted/50 via-muted/30 to-background">
    <div className="mx-auto flex max-w-[64rem] flex-col items-center gap-8">
      <div className="space-y-4 text-center">
        <div className="inline-flex items-center rounded-full border bg-gradient-to-r from-background to-muted/50 px-4 py-2 text-sm font-medium shadow-lg backdrop-blur-sm hover:shadow-xl transition-all group">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 mr-2 group-hover:bg-primary/20 transition-colors">
            <BarChart3 className="h-3.5 w-3.5 text-primary" />
          </div>
          What you'll learn
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
          Explanatory insights, not vanity metrics
        </h2>
        <p className="max-w-[42rem] leading-normal text-muted-foreground sm:text-lg sm:leading-8">
          Here's a preview of the insights we provide:
        </p>
      </div>
      
      <div className="grid gap-6 md:grid-cols-2 mt-12">
        <div className="group relative overflow-hidden rounded-2xl border-2 bg-gradient-to-br from-card to-card/50 p-8 shadow-xl transition-all hover:shadow-2xl hover:border-primary/50 hover:-translate-y-2 hover:scale-[1.02]">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-2xl -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-700" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary/80 text-primary-foreground text-sm font-bold shadow-lg group-hover:scale-110 transition-transform">
                <Zap className="h-6 w-6" />
              </div>
              <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">AI Result</span>
            </div>
            <h3 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">AI recommends: Competitor X</h3>
            <p className="text-muted-foreground leading-7">
              When users ask <span className="font-semibold text-foreground">"best project management tool for remote teams,"</span> ChatGPT recommends your competitor instead of you.
            </p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="group relative overflow-hidden rounded-2xl border-2 bg-gradient-to-br from-card to-card/80 p-6 shadow-lg transition-all hover:shadow-xl hover:border-primary/50 hover:-translate-y-2 hover:scale-[1.02]">
            <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-primary to-primary/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="flex items-start gap-4 relative z-10">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-bold text-sm shrink-0 shadow-md group-hover:scale-110 transition-transform">
                1
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Reason 1</div>
                <h4 className="font-bold mb-2 text-lg group-hover:text-primary transition-colors">Clearer positioning</h4>
                <p className="text-sm text-muted-foreground leading-6">Their homepage immediately communicates who they serve.</p>
              </div>
            </div>
          </div>
          
          <div className="group relative overflow-hidden rounded-2xl border-2 bg-gradient-to-br from-card to-card/80 p-6 shadow-lg transition-all hover:shadow-xl hover:border-primary/50 hover:-translate-y-2 hover:scale-[1.02]">
            <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-primary to-primary/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="flex items-start gap-4 relative z-10">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-bold text-sm shrink-0 shadow-md group-hover:scale-110 transition-transform">
                2
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Reason 2</div>
                <h4 className="font-bold mb-2 text-lg group-hover:text-primary transition-colors">Comparison content</h4>
                <p className="text-sm text-muted-foreground leading-6">They have dedicated comparison pages that AI models cite.</p>
              </div>
            </div>
          </div>
          
          <div className="group relative overflow-hidden rounded-2xl border-2 bg-gradient-to-br from-card to-card/80 p-6 shadow-lg transition-all hover:shadow-xl hover:border-primary/50 hover:-translate-y-2 hover:scale-[1.02]">
            <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-primary to-primary/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="flex items-start gap-4 relative z-10">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 text-primary font-bold text-sm shrink-0 shadow-md group-hover:scale-110 transition-transform">
                3
              </div>
              <div className="flex-1">
                <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Reason 3</div>
                <h4 className="font-bold mb-2 text-lg group-hover:text-primary transition-colors">Pricing clarity</h4>
                <p className="text-sm text-muted-foreground leading-6">Their pricing is transparent and AI can easily explain it.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const HowItWorksSection = () => (
  <section id="how-it-works" className="relative container space-y-6 py-20 md:py-32">
    <div className="absolute inset-0 -z-10 bg-gradient-to-b from-transparent via-muted/5 to-transparent" />
    <div className="mx-auto flex max-w-[64rem] flex-col items-center gap-8">
      <div className="space-y-4 text-center">
        <div className="inline-flex items-center rounded-full border bg-gradient-to-r from-muted to-muted/80 px-4 py-2 text-sm font-medium shadow-md backdrop-blur-sm hover:shadow-lg transition-all group">
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 mr-2 group-hover:bg-primary/20 transition-colors group-hover:rotate-90 transition-transform duration-500">
            <Settings className="h-3.5 w-3.5 text-primary" />
          </div>
          How it works
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
          Simple process, actionable insights
        </h2>
      </div>
      
      <div className="grid gap-8 md:grid-cols-3 mt-16">
        <div className="group relative flex flex-col gap-6 p-8 rounded-2xl border-2 bg-gradient-to-br from-card to-card/80 shadow-xl transition-all hover:shadow-2xl hover:border-primary/50 hover:-translate-y-3 hover:scale-[1.02] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl -translate-y-20 translate-x-20 group-hover:scale-150 transition-transform duration-700" />
          <div className="relative z-10">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-primary-foreground text-2xl font-bold shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
              <Zap className="h-8 w-8" />
            </div>
            <div className="space-y-3">
              <h3 className="text-2xl font-bold group-hover:text-primary transition-colors">Run AI prompts</h3>
              <p className="text-muted-foreground leading-7">
                We run high-intent prompts in AI assistants (ChatGPT, Perplexity, Claude) that your target customers would actually ask.
              </p>
            </div>
          </div>
        </div>
        
        <div className="group relative flex flex-col gap-6 p-8 rounded-2xl border-2 bg-gradient-to-br from-card to-card/80 shadow-xl transition-all hover:shadow-2xl hover:border-primary/50 hover:-translate-y-3 hover:scale-[1.02] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl -translate-y-20 translate-x-20 group-hover:scale-150 transition-transform duration-700" />
          <div className="relative z-10">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-primary-foreground text-2xl font-bold shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
              <TrendingUp className="h-8 w-8" />
            </div>
            <div className="space-y-3">
              <h3 className="text-2xl font-bold group-hover:text-primary transition-colors">Compare results</h3>
              <p className="text-muted-foreground leading-7">
                We compare how your product appears vs competitors across different AI models and prompt variations.
              </p>
            </div>
          </div>
        </div>
        
        <div className="group relative flex flex-col gap-6 p-8 rounded-2xl border-2 bg-gradient-to-br from-card to-card/80 shadow-xl transition-all hover:shadow-2xl hover:border-primary/50 hover:-translate-y-3 hover:scale-[1.02] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl -translate-y-20 translate-x-20 group-hover:scale-150 transition-transform duration-700" />
          <div className="relative z-10">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary via-primary/90 to-primary/80 text-primary-foreground text-2xl font-bold shadow-xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <div className="space-y-3">
              <h3 className="text-2xl font-bold group-hover:text-primary transition-colors">Get explanations</h3>
              <p className="text-muted-foreground leading-7">
                We explain why AI prefers certain products and what you can change to improve your recommendations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const WhoItsForSection = () => (
  <section className="relative container space-y-6 py-20 md:py-32 bg-gradient-to-b from-muted/50 via-muted/30 to-background">
    <div className="mx-auto flex max-w-[64rem] flex-col items-center gap-8">
      <div className="grid gap-8 md:grid-cols-2 md:gap-16 items-center">
        <div className="space-y-6">
          <div className="inline-flex items-center rounded-full border bg-gradient-to-r from-background to-muted/50 px-4 py-2 text-sm font-medium shadow-lg backdrop-blur-sm hover:shadow-xl transition-all group">
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 mr-2 group-hover:bg-primary/20 transition-colors group-hover:scale-110 transition-transform">
              <Target className="h-3.5 w-3.5 text-primary" />
            </div>
            Who it's for
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
            Built for founder-led SaaS
          </h2>
        </div>
        
        <div className="space-y-4 w-full">
          <div className="group flex items-start gap-4 rounded-2xl border-2 bg-gradient-to-br from-card to-card/80 p-6 shadow-lg transition-all hover:shadow-xl hover:border-primary/50 hover:-translate-x-2 hover:scale-[1.02]">
            <div className="mt-1 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/10 shrink-0 group-hover:scale-110 transition-transform">
              <CheckCircle2 className="h-5 w-5 text-primary" />
            </div>
            <p className="text-base font-medium leading-7 group-hover:text-primary transition-colors">Founder-led B2B SaaS companies</p>
          </div>
          <div className="group flex items-start gap-4 rounded-2xl border-2 bg-gradient-to-br from-card to-card/80 p-6 shadow-lg transition-all hover:shadow-xl hover:border-primary/50 hover:-translate-x-2 hover:scale-[1.02]">
            <div className="mt-1 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/10 shrink-0 group-hover:scale-110 transition-transform">
              <CheckCircle2 className="h-5 w-5 text-primary" />
            </div>
            <p className="text-base font-medium leading-7 group-hover:text-primary transition-colors">5–50 person teams</p>
          </div>
          <div className="group flex items-start gap-4 rounded-2xl border-2 bg-gradient-to-br from-card to-card/80 p-6 shadow-lg transition-all hover:shadow-xl hover:border-primary/50 hover:-translate-x-2 hover:scale-[1.02]">
            <div className="mt-1 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-primary/10 shrink-0 group-hover:scale-110 transition-transform">
              <CheckCircle2 className="h-5 w-5 text-primary" />
            </div>
            <p className="text-base font-medium leading-7 group-hover:text-primary transition-colors">Content / SEO dependent growth</p>
          </div>
          <div className="rounded-xl border-2 border-dashed bg-gradient-to-br from-muted/50 to-muted/30 p-4 ml-12 shadow-sm">
            <p className="text-sm text-muted-foreground italic">
              Not for companies with large AI or SEO research teams.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const GetStartedSection = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    website: '',
    competitor: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Submit form and show thank you message immediately
    await submitContactForm(formData);
    setIsSubmitted(true);
    setIsSubmitting(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  if (isSubmitted) {
    return (
      <section id="get-started" className="container space-y-6 py-20 md:py-32">
        <div className="mx-auto flex max-w-[42rem] flex-col items-center gap-6 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
            <svg className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl">
            Thank you
          </h2>
          <p className="max-w-[42rem] leading-normal text-muted-foreground sm:text-lg sm:leading-7">
            We'll run your AI recommendation check and share the insights within 48 hours.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="get-started" className="relative container space-y-6 py-20 md:py-32 overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-muted/20 via-background to-background" />
      <div className="mx-auto flex max-w-[64rem] flex-col items-center gap-8">
        <div className="grid gap-8 md:grid-cols-2 md:gap-16 items-start">
          <div className="space-y-6">
            <div className="inline-flex items-center rounded-full border bg-gradient-to-r from-muted to-muted/80 px-4 py-2 text-sm font-medium shadow-md backdrop-blur-sm hover:shadow-lg transition-all group">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 mr-2 group-hover:bg-primary/20 transition-colors group-hover:-translate-y-1 transition-transform">
                <Rocket className="h-3.5 w-3.5 text-primary" />
              </div>
              Get started
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
              Get your free AI recommendation check
            </h2>
            <p className="max-w-[42rem] leading-normal text-muted-foreground sm:text-lg sm:leading-8">
              We'll manually check how AI assistants recommend your product vs your competitors.
            </p>
          </div>
          
          <div className="relative rounded-2xl border-2 bg-gradient-to-br from-card to-card/80 p-8 shadow-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5 opacity-50" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-32 translate-x-32" />
            <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
              <div className="space-y-2">
                <Label htmlFor="companyName" className="text-sm font-semibold">Company name</Label>
                <Input
                  id="companyName"
                  name="companyName"
                  type="text"
                  required
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Your company"
                  className="h-12 border-2 focus:border-primary/50 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="website" className="text-sm font-semibold">Website</Label>
                <Input
                  id="website"
                  name="website"
                  type="url"
                  required
                  placeholder="https://yourwebsite.com"
                  value={formData.website}
                  onChange={handleChange}
                  className="h-12 border-2 focus:border-primary/50 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="competitor" className="text-sm font-semibold">Main competitor</Label>
                <Input
                  id="competitor"
                  name="competitor"
                  type="text"
                  required
                  value={formData.competitor}
                  onChange={handleChange}
                  placeholder="Competitor name"
                  className="h-12 border-2 focus:border-primary/50 transition-colors"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-semibold">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  className="h-12 border-2 focus:border-primary/50 transition-colors"
                />
              </div>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full h-12 text-base shadow-lg hover:shadow-xl bg-gradient-to-r from-primary to-primary/90 hover:scale-[1.02] transition-all group"
                size="lg"
              >
                {isSubmitting ? 'Submitting...' : (
                  <>
                    Submit
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                We'll run a manual AI recommendation check and share the insights.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer className="relative border-t bg-gradient-to-b from-muted/50 via-muted/30 to-background py-12 md:py-16 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent" />
    <div className="container flex flex-col items-center justify-between gap-6 md:flex-row relative z-10">
      <Logo />
      <div className="flex flex-col items-center gap-2 md:items-end">
        <p className="text-sm font-medium text-foreground">
          AI Recommendation Audits for Founder-Led SaaS
        </p>
        <p className="text-xs text-muted-foreground">
          © 2026 whyAIrecommend. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main>
        <HeroSection />
        <ProblemSection />
        <SampleResultsSection />
        <HowItWorksSection />
        <WhoItsForSection />
        <GetStartedSection />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
